import React, { useState } from "react";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import Table from "react-bootstrap/Table";
import { set } from "react-hook-form";
import InventoryHistory from "../common/InventoryHistory";
import JajotApi from "../../api/JajotApi";

const TableBody = ({
  headersState,
  sortConfig,
  toggleSort,
  currentRows,
  selectedRows,
  handleRowSelect,
  areAllRowsSelected,
  handleSelectAll,
  renderCell,
  permission,
  onDelete,
  onStatusChange,
  handleRowClick,
  handleEdit,
}) => {
  const visibleHeaders = headersState.filter((h) => h.show);
  const [showModal, setShowModal] = useState(false);
  const [modelContext, setModelContext] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const isRowSelected = (row) => selectedRows.some((r) => r.Id === row.Id);

  const renderSortIcon = (h) => {
    if (sortConfig.key !== h.key) return null;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const confirmDelete = (id) => {
    setModelContext({
      title: "Confirm Delete",
      body: <span>Are you sure you want to delete this record?</span>,
      onConfirm: () => handleConfirm(id), // pass id here
      confirmLabel: "Delete",
      confirmVariant: "danger",
      size: "md",
    });
    setShowModal(true);
  };

  const showInventoryHistory = async (row) => {
    setModelContext({
      title: "Inventory History",
      subTitle: row.Name,
      body: (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ),
    });
    setShowModal(true);

    try {
      const response = await JajotApi.getStockItemsVoucherEntries(row.Id);
      const entries = response.records || [];
      const historyData = {
        stock: row.Closing_Quantity__c || 0,
        amount: row.Closing_Amount__c || 0,
        purchaseTransactions: [],
        salesTransactions: [],
      };

      entries?.forEach((entry) => {
        const tx = {
          date: entry.date,
          quantity: entry.quantity,
          rate: entry.rate,
          amount: entry.amount,
        };

        if (entry.type === "Purchase") {
          historyData.purchaseTransactions.push(tx);
        } else {
          historyData.salesTransactions.push(tx);
        }
      });

      setModelContext({
        title: "Inventory History",
        subTitle: row.Name,
        body: <InventoryHistory data={historyData} />,
      });
    } catch (err) {
      console.error("Error fetching inventory history:", err);

      setModelContext({
        title: "Inventory History",
        subTitle: row.Name,
        body: (
          <div style={{ color: "red" }}>Failed to load inventory history.</div>
        ),
      });
    }
  };

  const handleConfirm = (id) => {
    console.log("Delete id:", id);
    onDelete?.(id);
    setShowModal(false);
    setModelContext({});
  };

  const renderActions = (row) => {
    if (!permission.update && !permission.delete && !permission.history)
      return null;

    return (
      <td className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
        {permission.update && (
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEdit?.(row)}
            title="Edit"
          >
            <i className="fa fa-pencil"></i>
          </Button>
        )}
        {permission.delete && (
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => confirmDelete(row.Id)}
            title="Delete"
          >
            <i className="fa fa-trash"></i>
          </Button>
        )}
        {permission.history && (
          <Button
            size="sm"
            variant="outline-success"
            onClick={() => showInventoryHistory(row)}
            title="Inventory History"
          >
            <i className="fa fa-line-chart"></i>
          </Button>
        )}
      </td>
    );
  };

  return (
    <>
      <div className="table-responsive">
        <Table striped hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "40px" }}>
                <Form.Check
                  type="checkbox"
                  checked={areAllRowsSelected}
                  onChange={handleSelectAll}
                />
              </th>
              {visibleHeaders.map((h) => (
                <th
                  key={h.key}
                  onClick={
                    h.sortable ? () => toggleSort(h.key, h.label) : undefined
                  }
                  className={`text-truncate ${h.sortable ? "cursor-pointer" : ""
                    } ${sortConfig.key === h.key ? "bg-success text-white" : ""}`}
                >
                  {h.label} {renderSortIcon(h)}
                </th>
              ))}
              {(permission.update ||
                permission.delete ||
                permission.history) && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((row) => (
                <tr key={row.Id} onClick={() => handleRowClick?.(row)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Form.Check
                      type="checkbox"
                      checked={isRowSelected(row)}
                      onChange={() => handleRowSelect(row)}
                    />
                  </td>
                  {visibleHeaders.map((h) => (
                    <td
                      key={h.key}
                      className="text-truncate small"
                      style={{ maxWidth: 200 }}
                    >
                      {renderCell(h, row, {onStatusChange })}
                    </td>
                  ))}
                  {renderActions(row)}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={visibleHeaders.length + 2}
                  className="text-center py-4"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size={modelContext.size || "lg"}
        scrollable
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{modelContext.title}</Modal.Title>
            {modelContext.subTitle && (
              <div className="small text-muted">{modelContext.subTitle}</div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="">
          {modelContext.body || (
            <div className="p-4">
              <div className="bg-light rounded-3 p-3 shadow-sm">
                <span className="text-secondary fs-6">
                  {modelContext.defaultBody || "No content available"}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>


        <Modal.Footer className="d-flex justify-content-between">
          {modelContext.footer || (
            <>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>
              {modelContext.onConfirm && (
                <Button
                  variant={modelContext.confirmVariant || "primary"}
                  onClick={modelContext.onConfirm}
                >
                  {modelContext.confirmLabel || "Confirm"}
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TableBody;
